import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2,
  ChevronLeft,
  Edit2,
  Users,
  Gem,
  MapPin,
  Phone,
  Eye,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { userService } from '@/services/user.service';
import { diamondsService } from '@/services/diamonds.service';
import { multiNivelService } from '@/services/multi-nivel.service';
import { SalesTable } from '@/components/SalesTable';
import { NetworkVisualization } from '@/components/NetworkVisualization';

interface User {
  id: number;
  cidade: string;
  codigo: number;
  cpf_cnpj: string;
  email: string;
  estado: string;
  fornecedor?: string;
  nome_fantasia?: string;
  nome_razao_social: string;
  role: string;
  idcoordenadora: number;
  pix?: string;
  phones?: Array<{
    telefone: string;
  }>;
  address?: {
    line_1: string;
    number: string;
    complement?: string;
    city: string;
    state: string;
    zip_code: string;
  };
  coordenadora?: {
    nome_razao_social: string;
  };
  totalSales?: number;
}

interface AdesaoResponse {
  id: number;
  status: 'pendente' | 'aprovado' | 'reprovado';
  diamantes: number;
}

const UserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userId = parseInt(id || '0');

  // Estados principais
  const [user, setUser] = useState<User | null>(null);
  const [coordenadora, setCoordenadora] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);

  // Diamonds e adesão
  const [saldo, setSaldo] = useState(0);
  const [statusAdesao, setStatusAdesao] = useState<string | null>(null);
  const [showVendas, setShowVendas] = useState(false);

  // Indicador (quem indicou)
  const [indicador, setIndicador] = useState<{ id: number; nome_razao_social: string; codigo: number; role: string } | null>(null);

  // Revendedoras coordenadas
  const [revendedoras, setRevendedoras] = useState<User[]>([]);
  const [coordenadoras, setCoordanadoras] = useState<any[]>([]);
  const [revenPage, setRevenPage] = useState(1);
  const REVEN_PAGE_SIZE = 10;

  // Modais de edição
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const [assignCoordOpen, setAssignCoordOpen] = useState(false);
  const [editPixOpen, setEditPixOpen] = useState(false);
  const [editIndicadorOpen, setEditIndicadorOpen] = useState(false);

  // Valores de entrada
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('');
  const [selectedCoordenadora, setSelectedCoordenadora] = useState('');
  const [newPix, setNewPix] = useState('');
  const [newIndicadorCodigo, setNewIndicadorCodigo] = useState('');

  // Confirmação
  const [confirmOpen, setConfirmOpen] = useState<{
    type: string;
    action: () => Promise<void>;
  } | null>(null);

  const roles = ['admin', 'coordenadora', 'revendedora', 'cliente'];

  const roleColors: { [key: string]: string } = {
    admin: 'bg-red-100 text-red-800',
    coordenadora: 'bg-blue-100 text-blue-800',
    revendedora: 'bg-yellow-100 text-yellow-800',
    cliente: 'bg-green-100 text-green-800',
  };

  const roleDisplayNames: { [key: string]: string } = {
    admin: 'Administrator',
    coordenadora: 'Coordenadora',
    revendedora: 'Revendedora',
    cliente: 'Cliente',
  };

  useEffect(() => {
    if (userId) {
      loadUserDetails();
    }
  }, [userId]);

  const loadUserDetails = async () => {
    setLoading(true);
    try {
      const userData = await userService.getUserDataById(userId);
      setUser(userData);
      setNewName(userData.nome_razao_social);
      setNewEmail(userData.email);
      setNewRole(userData.role);
      setNewPix(userData.pix || '');

      // Carregar coordenadora se existir
      if (
        userData.idcoordenadora &&
        userData.idcoordenadora > 0 &&
        userData.role !== 'coordenadora' &&
        userData.role !== 'admin'
      ) {
        const coordData = await userService.getUserDataById(userData.idcoordenadora);
        setCoordenadora(coordData);
      }

      // Carregar adesão ao programa de diamantes
      try {
        const resp = await diamondsService.getAdesao(userId);
        // Verificar se resp é um array ou objeto direto
        const adesaoData = Array.isArray(resp) ? resp[0] : resp;
        
        if (adesaoData && adesaoData.status) {
          if (adesaoData.status === 'aprovado') {
            setSaldo(adesaoData.diamantes || 0);
            setStatusAdesao('aprovado');
          } else if (adesaoData.status === 'pendente') {
            setStatusAdesao('pendente');
          } else if (adesaoData.status === 'reprovado') {
            setStatusAdesao('reprovado');
          }
        } else {
          setStatusAdesao(null);
        }
      } catch (error) {
        console.error('Erro ao carregar adesão:', error);
        setStatusAdesao(null);
      }

      // Carregar indicador (quem indicou este usuário)
      try {
        const networkData = await multiNivelService.getNetworkStructure(userId);
        if (networkData?.indicador) {
          setIndicador(networkData.indicador);
        }
      } catch (error) {
        console.error('Erro ao carregar indicador:', error);
      }

      // Se for coordenadora, carregar revendedoras coordenadas
      if (userData.role === 'coordenadora') {
        const revenData = await userService.getRevendedorasCoordenadas(userData.id);
        setRevendedoras(revenData || []);
      }

      // Definir se deve mostrar tabela de vendas (para todos exceto cliente)
      setShowVendas(userData.role !== 'cliente');

      // Carregar lista de coordenadoras
      const coordList = await userService.getCoordenadoras();
      setCoordanadoras(coordList || []);
    } catch (error) {
      console.error('Erro ao carregar detalhes do usuário:', error);
      toast.error('Erro ao carregar detalhes do usuário');
      navigate('/usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      toast.error('Digite um nome');
      return;
    }

    setLoadingAction(true);
    try {
      await userService.updateUser({
        id: user!.id,
        name: newName,
      });
      toast.success('Nome atualizado com sucesso');
      setEditNameOpen(false);
      await loadUserDetails();
    } catch (error) {
      console.error('Erro ao atualizar nome:', error);
      toast.error('Erro ao atualizar nome');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim()) {
      toast.error('Digite um email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error('Email inválido');
      return;
    }

    setLoadingAction(true);
    try {
      await userService.updateUser({
        id: user!.id,
        email: newEmail,
      });
      toast.success('Email atualizado com sucesso');
      setEditEmailOpen(false);
      await loadUserDetails();
    } catch (error) {
      console.error('Erro ao atualizar email:', error);
      toast.error('Erro ao atualizar email');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleChangeRole = async () => {
    if (newRole === user!.role) {
      toast.error('Selecione uma função diferente');
      return;
    }

    setLoadingAction(true);
    try {
      await userService.updateUser({
        id: user!.id,
        role: newRole,
      });
      toast.success('Função atualizada com sucesso');
      setEditRoleOpen(false);
      await loadUserDetails();
    } catch (error) {
      console.error('Erro ao atualizar função:', error);
      toast.error('Erro ao atualizar função');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleAssignCoordenadora = async () => {
    if (newRole === 'coordenadora' || newRole === 'admin') {
      toast.error('Não é possível atribuir coordenadora para admin ou outra coordenadora');
      return;
    }

    if (!selectedCoordenadora) {
      toast.error('Selecione uma coordenadora');
      return;
    }

    setLoadingAction(true);
    try {
      await userService.atribuirCoordenadora(
        parseInt(selectedCoordenadora),
        user!.id
      );
      toast.success('Coordenadora atribuída com sucesso');
      setAssignCoordOpen(false);
      setSelectedCoordenadora('');
      await loadUserDetails();
    } catch (error) {
      console.error('Erro ao atribuir coordenadora:', error);
      toast.error('Erro ao atribuir coordenadora');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleUpdatePix = async () => {
    if (!newPix.trim()) {
      toast.error('Digite uma chave PIX');
      return;
    }

    if (newPix.trim().length < 3) {
      toast.error('Chave PIX deve ter no mínimo 3 caracteres');
      return;
    }

    setLoadingAction(true);
    try {
      await userService.updatePix(user!.id, newPix.trim());
      toast.success('Chave PIX atualizada com sucesso');
      setEditPixOpen(false);
      await loadUserDetails();
    } catch (error) {
      console.error('Erro ao atualizar PIX:', error);
      const errorMessage = (error as any)?.response?.data?.message || 'Erro ao atualizar PIX';
      toast.error(errorMessage);
    } finally {
      setLoadingAction(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="w-full p-6">
        <Button
          variant="outline"
          onClick={() => navigate('/usuarios')}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6 text-center">
            <p className="text-gray-600">Usuário não encontrado</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      {/* Botão Voltar */}
      <Button
        variant="outline"
        onClick={() => navigate('/usuarios')}
        className="mb-2"
      >
        <ChevronLeft className="w-4 h-4 mr-2" />
        Voltar para Usuários
      </Button>

      {/* Card de Informações Principais */}
      <Card className="shadow-md">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <span className='text-muted-foreground'>#{user.codigo}</span> {user.nome_razao_social}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditNameOpen(true)}
                  title="Alterar nome"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </CardTitle>
              <CardDescription>{user.cpf_cnpj}</CardDescription>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Indicado(a) por</p>
              {indicador ? (
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">#{indicador.codigo}</Badge>
                  <span className="font-semibold">{indicador.nome_razao_social}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/usuarios/${indicador.id}`)}
                    title="Ver perfil do indicador"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setNewIndicadorCodigo('');
                      setEditIndicadorOpen(true);
                    }}
                    title="Alterar indicador"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm italic text-muted-foreground">Nenhum indicador</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setNewIndicadorCodigo('');
                      setEditIndicadorOpen(true);
                    }}
                    title="Atribuir indicador"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Seção 1: Nome, Email, CPF */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Nome/Razão Social
              </label>
              <div className="flex items-center justify-between gap-2">
                <p className="text-lg font-semibold">{user.nome_razao_social}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditNameOpen(true)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </div> */}

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Função
              </label>
              <div className="flex items-center gap-2">
                <Badge className={roleColors[user.role]}>
                  {roleDisplayNames[user.role]}
                </Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditRoleOpen(true)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Email
              </label>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold">{user.email}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditEmailOpen(true)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Chave PIX
              </label>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold">{user.pix || '--'}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditPixOpen(true)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                CPF/CNPJ
              </label>
              <p className="text-lg font-semibold">{user.cpf_cnpj}</p>
            </div> */}
          </div>

          {/* Seção 2: Código, Função, Coordenadora */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Código Único
              </label>
              <p className="text-lg font-semibold">{user.codigo}</p>
            </div> */}

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Coordenador(a)
              </label>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold">
                  {coordenadora?.nome_razao_social || '--'}
                </p>
                {user.role !== 'admin' && user.role !== 'coordenadora' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setAssignCoordOpen(true)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
             <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Telefone(s)
              </label>
              <div className="space-y-1">
                {user.phones && user.phones.length > 0 ? (
                  user.phones.map((phone, idx) => (
                    <p key={idx} className="text-lg font-semibold flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {phone.telefone}
                    </p>
                  ))
                ) : (
                  <p className="text-lg font-semibold">--</p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Cidade/UF
              </label>
              <p className="text-lg font-semibold">
                {user.cidade} - {user.estado}
              </p>
            </div>
          </div>

          {/* Seção 3: Diamantes e Adesão */}
          <div className="border-t pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-600">                 
                  Diamantes
                </label>
                <div className='flex items-center gap-2'>
                  <span className={`text-3xl font-bold ${saldo === 0 ? 'text-gray-400' : 'text-purple-600'}`}>{saldo}</span>
                   <Gem className="w-6 h-6 text-purple-700" />
                </div>
                <div className="mt-2">
                  {statusAdesao === null && (
                    <p className="text-sm text-gray-500 italic">Não participa do programa</p>
                  )}
                  {statusAdesao === 'pendente' && (
                    <Badge variant="outline" className="bg-blue-50">
                      Aprovação pendente
                    </Badge>
                  )}
                  {statusAdesao === 'reprovado' && (
                    <Badge variant="destructive" className="bg-red-50">
                      Reprovada no programa
                    </Badge>
                  )}
                </div>
              </div>

              {/* Endereço */}
              <div>
                <label className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Endereço
                </label>
                {user.address ? (
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">
                      {user.address.line_1}, {user.address.number}
                    </p>
                    {user.address.complement && (
                      <p className="text-sm text-gray-600">
                        Complemento: {user.address.complement}
                      </p>
                    )}
                    <p className="text-sm font-semibold">
                      {user.address.city}, {user.address.state}
                    </p>
                    <p className="text-sm text-gray-600">CEP: {user.address.zip_code}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Não preenchido</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Revendedoras (se for coordenadora) */}
      {user.role === 'coordenadora' && revendedoras.length > 0 && (() => {
        const totalPages = Math.ceil(revendedoras.length / REVEN_PAGE_SIZE);
        const paginadas = revendedoras.slice((revenPage - 1) * REVEN_PAGE_SIZE, revenPage * REVEN_PAGE_SIZE);
        return (
          <Card className="border-0 shadow-sm">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Revendedoras Coordenadas
                  <span className="text-sm font-normal text-muted-foreground">({revendedoras.length})</span>
                </CardTitle>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRevenPage(p => Math.max(1, p - 1))}
                      disabled={revenPage === 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-muted-foreground">{revenPage} / {totalPages}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRevenPage(p => Math.min(totalPages, p + 1))}
                      disabled={revenPage === totalPages}
                    >
                      Próxima
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b">
                      <TableHead className="font-semibold">Código</TableHead>
                      <TableHead className="font-semibold">Nome</TableHead>
                      <TableHead className="font-semibold">Total em Vendas</TableHead>
                      <TableHead className="font-semibold">Cidade/UF</TableHead>
                      <TableHead className="font-semibold text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginadas.map(reven => (
                      <TableRow key={reven.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{reven.codigo}</TableCell>
                        <TableCell>{reven.nome_razao_social}</TableCell>
                        <TableCell className="text-green-600 font-semibold">
                          R$ {reven.totalSales || '0'}
                        </TableCell>
                        <TableCell>
                          {reven.cidade} - {reven.estado}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/usuarios/${reven.id}`)}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <p className="text-xs text-muted-foreground text-right mt-2">
                  Exibindo {(revenPage - 1) * REVEN_PAGE_SIZE + 1}–{Math.min(revenPage * REVEN_PAGE_SIZE, revendedoras.length)} de {revendedoras.length}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Dialog Editar Nome */}
      <Dialog open={editNameOpen} onOpenChange={setEditNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar Nome</DialogTitle>
            <DialogDescription>
              Nome atual: <span className="font-semibold">{user.nome_razao_social}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Novo nome"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setEditNameOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateName}
                disabled={loadingAction || !newName.trim()}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Email */}
      <Dialog open={editEmailOpen} onOpenChange={setEditEmailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar Email</DialogTitle>
            <DialogDescription>
              Email atual: <span className="font-semibold">{user.email}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              type="email"
              placeholder="Novo email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
            />

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setEditEmailOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdateEmail}
                disabled={loadingAction || !newEmail.trim()}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar PIX */}
      <Dialog open={editPixOpen} onOpenChange={setEditPixOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar Chave PIX</DialogTitle>
            <DialogDescription>
              Chave PIX atual: <span className="font-semibold">{user.pix || 'Não definida'}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input
              placeholder="Nova chave PIX (mínimo 3 caracteres)"
              value={newPix}
              onChange={(e) => setNewPix(e.target.value)}
            />

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setEditPixOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleUpdatePix}
                disabled={loadingAction || newPix.trim().length < 3}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Trocar Função */}
      <Dialog open={editRoleOpen} onOpenChange={setEditRoleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trocar Função</DialogTitle>
            <DialogDescription>
              Função atual: <span className="font-semibold">{roleDisplayNames[user.role]}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione a nova função..." />
              </SelectTrigger>
              <SelectContent>
                {roles.map(role => (
                  <SelectItem key={role} value={role}>
                    {roleDisplayNames[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setEditRoleOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleChangeRole}
                disabled={loadingAction || newRole === user.role}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Atribuir/Alterar Coordenadora */}
      <Dialog open={assignCoordOpen} onOpenChange={setAssignCoordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{user.idcoordenadora > 0 ? 'Alterar' : 'Atribuir'} Coordenadora</DialogTitle>
            <DialogDescription>
              {coordenadora
                ? <>Coordenadora atual: <span className="font-semibold">{coordenadora.nome_razao_social}</span></>
                : 'Nenhuma coordenadora atribuída'}
              {' — '}Selecione a nova coordenadora para {user.nome_razao_social}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Select value={selectedCoordenadora} onValueChange={setSelectedCoordenadora}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione uma coordenadora..." />
              </SelectTrigger>
              <SelectContent>
                {coordenadoras.map(coord => (
                  <SelectItem key={coord.id} value={coord.id.toString()}>
                    {coord.nome_razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setAssignCoordOpen(false);
                  setSelectedCoordenadora('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAssignCoordenadora}
                disabled={loadingAction || !selectedCoordenadora}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Atribuindo...
                  </>
                ) : (
                  'Atribuir'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Alterar/Atribuir Indicador */}
      <Dialog open={editIndicadorOpen} onOpenChange={setEditIndicadorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{indicador ? 'Alterar' : 'Atribuir'} Indicador</DialogTitle>
            <DialogDescription>
              {indicador
                ? <>Indicador atual: <span className="font-semibold">#{indicador.codigo} — {indicador.nome_razao_social}</span></>
                : 'Nenhum indicador atribuído'}
              {' — '}Digite o código do novo indicador para {user.nome_razao_social}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Código do indicador</label>
              <Input
                type="number"
                placeholder="Ex: 1234"
                value={newIndicadorCodigo}
                onChange={(e) => setNewIndicadorCodigo(e.target.value)}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setEditIndicadorOpen(false);
                  setNewIndicadorCodigo('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!newIndicadorCodigo.trim()) {
                    toast.error('Digite o código do indicador');
                    return;
                  }
                  setLoadingAction(true);
                  try {
                    await multiNivelService.criarIndicacaoManual([{
                      codigo_indicante: parseInt(newIndicadorCodigo),
                      codigo_indicada: user.codigo
                    }]);
                    toast.success('Indicador atualizado com sucesso');
                    setEditIndicadorOpen(false);
                    setNewIndicadorCodigo('');
                    await loadUserDetails();
                  } catch (error) {
                    console.error('Erro ao atualizar indicador:', error);
                    toast.error('Erro ao atualizar indicador. Verifique se o código é válido.');
                  } finally {
                    setLoadingAction(false);
                  }
                }}
                disabled={loadingAction || !newIndicadorCodigo.trim()}
              >
                {loadingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Abas: Vendas e Rede de Indicação */}
      {user && (
        <Card className="shadow-md">
          <CardHeader className="border-b">
            <CardTitle>Dados Complementares</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <Tabs defaultValue={user.role === 'cliente' ? 'compras' : 'vendas'} className="w-full">
              <TabsList>
                {showVendas && <TabsTrigger value="vendas">Vendas</TabsTrigger>}
                {!showVendas && <TabsTrigger value="compras">Compras</TabsTrigger>}
                <TabsTrigger value="rede">Rede de Indicação</TabsTrigger>
              </TabsList>

              {showVendas && (
                <TabsContent value="vendas" className="mt-6">
                  <SalesTable userId={user.id} title="Vendas do Usuário" />
                </TabsContent>
              )}
              {!showVendas && (
                <TabsContent value="compras" className="mt-6">
                  <SalesTable userId={user.id} userRole={user.role} title="Compras do Usuário" />
                </TabsContent>
              )}

              <TabsContent value="rede" className="mt-6">
                <NetworkVisualization usuarioId={user.id} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserDetail;
